import { Component, OnInit, ViewChild } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { RouteEnum } from "../../shared/models/routes.model";
import { ServiceMetaData } from "../../shared/models/service-meta.model";
import { UiUser, UsersSearchParams } from "../../shared/models/ui-user.model";
import { PaginationParams } from "../../shared/models/common";
import { SharedService } from "../../shared/services/shared.service";
import { DataService } from "../../shared/services/data.service";
import { UsersService } from "../../shared/services/users.service";
import { Table } from "primeng/table";
import { Store } from "@ngrx/store";
import {
  lastSavedUser,
  mngSelectedClinic,
  plantList,
  roleList,
  selectedUser,
  users,
  usersSearchMetadata,
} from "src/app/shared/ngrx/ngrx-selectors";
import {
  EDIT_USER,
  GET_CLINIC_USERS,
  RUN_USERS_SEARCH,
  UPDATE_USER,
} from "src/app/shared/ngrx/ngrx-actions";
import { ofType, Actions } from "@ngrx/effects";
import { map, take, takeUntil } from "rxjs/operators";
import { UsersFiltersComponent } from "./users-filters/users-filters.component";
import { UsersListComponent } from "./users-list/users-list.component";
import { CommonModule } from "@angular/common";
import { LoaderComponent } from "src/app/shared/components/ui/loader/loader.component";
import { TranslateModule } from "@ngx-translate/core";

@Component({
  selector: "app-mng-users",
  templateUrl: "./mng-users.component.html",
  styleUrls: ["./mng-users.component.sass"],
  imports: [
    UsersFiltersComponent,
    UsersListComponent,
    CommonModule,
    LoaderComponent,
    TranslateModule,
  ],
  standalone: true,
})
export class MngUsersComponent implements OnInit {
  usersLoading: boolean;
  searchSubscription: Subscription;
  routeEnum: RouteEnum = new RouteEnum();
  newUser: UiUser = new UiUser();
  editUserTitle: string;
  numberOfRows: number = 20;
  rangeFrom: number = 1;
  rangeTo: number = this.numberOfRows;
  totalRecords: number;
  destroy$ = new Subject();

  @ViewChild(Table) tableComponent: Table;

  get userSearchMetaData(): ServiceMetaData {
    return this.store.selectSignal(usersSearchMetadata)() || {};
  }

  get displayUsers(): UiUser[] {
    return this.store.selectSignal(users)();
  }

  get selectedUser(): UiUser {
    return this.store.selectSignal(selectedUser)();
  }

  searchParams: UsersSearchParams = {
    rangeTo: this.rangeTo,
    rangeFrom: this.rangeFrom,
    searchId: null,
  };

  constructor(
    private readonly store: Store,
    private usersService: UsersService,
    private actions$: Actions,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.loadInitialData();

    if (this.store.selectSignal(mngSelectedClinic)()) {
      this.getUsersByClinic();
    } else {
      if (!this.userSearchMetaData.searchId) {
        this.runUsersSearch(true);
      }
    }

    this.actions$
      .pipe(
        ofType(UPDATE_USER),
        takeUntil(this.destroy$),
        map(({}) => {
          this.runUsersSearch(false);
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(GET_CLINIC_USERS),
        takeUntil(this.destroy$),
        map(({}) => {
          this.getUsersByClinic();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
  }

  getUsersByClinic() {
    this.searchParams = {
      clinic: this.store.selectSignal(mngSelectedClinic)(),
    };
    this.runUsersSearch(true);
  }

  loadInitialData() {
    if (this.store.selectSignal(plantList)().length == 0) this.getPlantList();
  }

  getPlantList() {
    this.dataService.getPlantList().subscribe();
  }

  getUsersBySearch(params: UsersSearchParams) {
    console.log(params);
    this.searchParams = Object.assign(
      this.searchParams,
      this.store.selectSignal(usersSearchMetadata)(),
      params
    );

    this.searchParams.rangeFrom = 1;
    this.searchParams.rangeTo = this.numberOfRows;

    this.runUsersSearch(true);
  }

  loadDataOnPagination(event: any) {
    if (
      !this.displayUsers ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId =
      this.store.selectSignal(usersSearchMetadata)().searchId;

    this.runUsersSearch(false);
  }

  runUsersSearch(newSearch: boolean) {
    this.usersLoading = true;

    var element = document.getElementById("usersSearch");
    if (element) element.scrollIntoView(true);

    this.store.dispatch(
      RUN_USERS_SEARCH({
        newSearch: newSearch,
      })
    );

    this.usersService.getUsers(this.searchParams).subscribe(
      () => {
        this.usersLoading = false;
      },
      () => (this.usersLoading = false)
    );
  }

  editUser(user: UiUser) {
    this.store.dispatch(
      EDIT_USER({
        user: user,
      })
    );
  }

  onCreateUserClicked() {
    this.newUser = new UiUser();
    this.newUser.activeSw = true;

    let lastUser = this.store.selectSignal(lastSavedUser)();
    if (lastUser) {
      this.newUser.role = lastUser.role;
      this.newUser.clinic = lastUser.clinic;
      this.newUser.plant = lastUser.plant;
      this.newUser.doctor = lastUser.doctor;
    }

    this.store.dispatch(
      EDIT_USER({
        user: this.newUser,
      })
    );
  }
}
